import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  CreditCard,
  Lock,
  Shield,
  CheckCircle,
  AlertCircle,
  Clock,
  DollarSign,
  User,
  RefreshCw
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { MetaPixel } from "../utils/metaPixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentLinkPage = () => {
  const { linkId } = useParams();
  const navigate = useNavigate();
  const [linkData, setLinkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLinkData();
  }, [linkId]);
  
  // Track view content when link data loads
  useEffect(() => {
    if (linkData) {
      MetaPixel.trackViewContent({
        content_name: linkData.product_name,
        content_category: 'Payment Link',
        value: linkData.amount,
        currency: 'USD'
      });
    }
  }, [linkData]);

  const fetchLinkData = async () => {
    try {
      const res = await axios.get(`${API}/payments/link/${linkId}`);
      setLinkData(res.data);
    } catch (error) {
      setError(error.response?.data?.detail || "Link de pago no válido");
    } finally {
      setLoading(false);
    }
  };

  const handlePay = async () => {
    // Track initiate checkout
    MetaPixel.trackInitiateCheckout({
      plan_name: linkData?.product_name,
      plan_id: linkData?.product_id || linkId,
      price: linkData?.amount,
      source: 'payment_link'
    });
    
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/payments/link/${linkId}/checkout`);
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al procesar pago");
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-navy-50 to-navy-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-navy-50 to-navy-100 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-navy-900 mb-2">
            Link no válido
          </h1>
          <p className="text-navy-600 mb-6">{error}</p>
          <Button onClick={() => navigate("/")} variant="outline">
            Volver al inicio
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 to-navy-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 px-6 py-8 text-white text-center">
          <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-full flex items-center justify-center mx-auto mb-4">
            <CreditCard className="w-8 h-8" />
          </div>
          <h1 className="font-heading text-2xl font-bold mb-2">Pago Seguro</h1>
          <p className="text-emerald-100">Divídelo Consultoría Migratoria</p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Product Details */}
          <div className="bg-navy-50 rounded-xl p-5">
            <h2 className="font-semibold text-navy-900 mb-2">{linkData?.product_name}</h2>
            <p className="text-navy-600 text-sm">{linkData?.description}</p>
          </div>

          {/* Vendedor Info */}
          <div className="flex items-center gap-3 p-4 border border-navy-200 rounded-lg">
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-navy-500">Generado por</p>
              <p className="font-medium text-navy-900">{linkData?.vendedor_name}</p>
            </div>
          </div>

          {/* Amount */}
          <div className="text-center py-4">
            <p className="text-navy-500 text-sm mb-1">Total a pagar</p>
            <p className="text-4xl font-bold text-emerald-600">
              ${linkData?.amount?.toFixed(2)}
              <span className="text-lg font-normal text-navy-500"> USD</span>
            </p>
          </div>

          {/* Security Badges */}
          <div className="flex items-center justify-center gap-6 py-4 border-t border-navy-100">
            <div className="flex items-center gap-2 text-navy-500 text-sm">
              <Lock className="w-4 h-4" />
              <span>SSL Seguro</span>
            </div>
            <div className="flex items-center gap-2 text-navy-500 text-sm">
              <Shield className="w-4 h-4" />
              <span>Stripe</span>
            </div>
          </div>

          {/* Pay Button */}
          <Button
            onClick={handlePay}
            disabled={processing}
            className="w-full bg-emerald-600 hover:bg-emerald-700 py-6 text-lg"
            data-testid="pay-now-btn"
          >
            {processing ? (
              <>
                <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                Procesando...
              </>
            ) : (
              <>
                <CreditCard className="w-5 h-5 mr-2" />
                Pagar Ahora
              </>
            )}
          </Button>

          <p className="text-xs text-center text-navy-400">
            Al hacer clic en "Pagar Ahora", serás redirigido a la plataforma segura de Stripe para completar tu pago.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default PaymentLinkPage;
